/**
 * Calendar Adapters
 *
 * Transform view models to react-big-calendar event format.
 *
 * DDD Note: This is a PRESENTATION concern - adapting view models
 * to library-specific event format. Isolates calendar library coupling.
 */

import type { JobCardViewModel } from '@/application/service-provider/view-models/JobListViewModel';

/**
 * Calendar event format for react-big-calendar
 */
export interface CalendarEvent {
  id: string;
  title: string;
  start: Date;
  end: Date;
  resource: JobCardViewModel; // Store full view model for access in event handlers
}

/**
 * Transform a single job view model to calendar event
 *
 * Uses existing view model data - no new transformations.
 * Start/end times derived from existing timestamp fields.
 *
 * @param job - Job view model to transform
 * @returns Calendar event ready for react-big-calendar
 */
export function toCalendarEvent(job: JobCardViewModel): CalendarEvent {
  // startDateTimestamp is in Unix seconds, convert to milliseconds
  const start = new Date(job.startDateTimestamp * 1000);

  // Use target completion if available, otherwise default to 1 hour after start
  const end = job.targetCompletionDateTimestamp
    ? new Date(job.targetCompletionDateTimestamp * 1000)
    : new Date(job.startDateTimestamp * 1000 + 3600000); // +1 hour in milliseconds

  return {
    id: job.id,
    title: job.displayName,
    start,
    end,
    resource: job, // Full view model available for rendering/interaction
  };
}

/**
 * Transform array of job view models to calendar events
 *
 * @param jobs - Array of job view models
 * @returns Array of calendar events
 */
export function fromJobsToCalendarEvents(
  jobs: JobCardViewModel[]
): CalendarEvent[] {
  return jobs.map(toCalendarEvent);
}

/**
 * Extract job view model from calendar event
 *
 * Helper for event handlers that need access to full job data.
 *
 * @param event - Calendar event
 * @returns Original job view model
 */
export function getJobFromEvent(event: CalendarEvent): JobCardViewModel {
  return event.resource;
}

/**
 * Get event style based on job status
 *
 * Uses existing statusBadge.color from view model for consistency.
 *
 * @param event - Calendar event
 * @returns Style object for react-big-calendar
 */
export function getEventStyle(event: CalendarEvent): {
  style: { backgroundColor: string; borderColor: string };
} {
  const job = event.resource;

  // Map status badge colors to calendar event colors
  const colorMap: Record<string, { backgroundColor: string; borderColor: string }> = {
    blue: { backgroundColor: '#DBEAFE', borderColor: '#3B82F6' },    // scheduled
    yellow: { backgroundColor: '#FEF3C7', borderColor: '#F59E0B' },  // in_progress
    green: { backgroundColor: '#D1FAE5', borderColor: '#10B981' },   // completed
    red: { backgroundColor: '#FEE2E2', borderColor: '#EF4444' },     // cancelled
    gray: { backgroundColor: '#F3F4F6', borderColor: '#6B7280' },    // pending
  };

  const colors = colorMap[job.statusBadge.color] ?? colorMap.gray ?? {
    backgroundColor: '#F3F4F6',
    borderColor: '#6B7280',
  };

  return { style: colors };
}
